import {
  NJButton,
  NJFormItem,
  NJHeading,
  NJIconButton,
  NJInlineMessage,
  NJSpinner,
  NJText,
} from "@engie-group/fluid-design-system-react";
import type { ColDef, ICellRendererParams } from "ag-grid-community";
import { useMemo, useState } from "react";
import { ConfirmModal } from "@/components/ConfirmModal";
import { DataTable } from "@/components/DataTable";
import {
  type ReferentialEntry,
  type ReferentialKind,
  useAddReferential,
  useReferential,
  useRemoveReferential,
} from "./useReferentials";

function ActionsCell({
  data,
  kind,
}: ICellRendererParams<ReferentialEntry> & { kind: ReferentialKind }) {
  const remove = useRemoveReferential();
  const [confirmOpen, setConfirmOpen] = useState(false);
  if (!data) return null;
  return (
    <>
      <NJIconButton
        icon="delete"
        variant="destructive"
        label={`Delete ${data.value}`}
        isDisabled={remove.isPending}
        onClick={() => setConfirmOpen(true)}
      />
      <ConfirmModal
        open={confirmOpen}
        setOpen={setConfirmOpen}
        title="Delete entry"
        isPending={remove.isPending}
        onConfirm={() =>
          remove.mutate(
            { kind, value: data.value },
            { onSuccess: () => setConfirmOpen(false) },
          )
        }
      >
        Delete <strong>{data.value}</strong>? This action cannot be undone.
      </ConfirmModal>
    </>
  );
}

/** Generic admin referential manager (skill categories / agent use cases): add, list with a
 * "used by" count, and delete. */
export function AdminReferentialPage({
  kind,
  title,
  valueLabel,
  usedByHeader,
}: {
  kind: ReferentialKind;
  title: string;
  valueLabel: string;
  usedByHeader: string;
}) {
  const { data, isLoading, isError } = useReferential(kind);
  const add = useAddReferential();
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | undefined>();

  const columnDefs = useMemo<ColDef<ReferentialEntry>[]>(
    () => [
      { headerName: valueLabel, field: "value", flex: 2 },
      { headerName: usedByHeader, field: "usedBy", flex: 1 },
      {
        headerName: "Actions",
        flex: 1,
        sortable: false,
        cellRenderer: (params: ICellRendererParams<ReferentialEntry>) => (
          <ActionsCell {...params} kind={kind} />
        ),
      },
    ],
    [valueLabel, usedByHeader, kind],
  );

  const submit = () => {
    const trimmed = value.trim();
    if (!trimmed) {
      setError("This field is required.");
      return;
    }
    setError(undefined);
    add.mutate({ kind, value: trimmed }, { onSuccess: () => setValue("") });
  };

  return (
    <div className="flex flex-col gap-4">
      <NJHeading as="h1" scale="lg">
        {title}
      </NJHeading>

      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[280px]">
          <NJFormItem
            id="referential-value"
            label={valueLabel}
            isRequired
            hasError={Boolean(error)}
            subscriptMessage={error}
            value={value}
            onChange={(event) =>
              setValue((event.target as HTMLInputElement).value)
            }
          />
        </div>
        <NJButton
          label="Add"
          icon="add"
          disabled={add.isPending}
          onClick={submit}
        />
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <NJSpinner aria-label="Loading" />
        </div>
      ) : isError ? (
        <NJInlineMessage variant="error">
          Could not load this referential.
        </NJInlineMessage>
      ) : (
        <>
          <DataTable<ReferentialEntry>
            rowData={data ?? []}
            columnDefs={columnDefs}
          />
          {data && data.length === 0 && (
            <NJText
              as="p"
              scale="sm"
              className="text-nj-neutral-secondary-default"
            >
              No entry yet.
            </NJText>
          )}
        </>
      )}
    </div>
  );
}

export function AdminSkillCategoriesPage() {
  return (
    <AdminReferentialPage
      kind="skill-categories"
      title="Administration — skill categories"
      valueLabel="Category"
      usedByHeader="Used by skills"
    />
  );
}

export function AdminUseCasesPage() {
  return (
    <AdminReferentialPage
      kind="agent-use-cases"
      title="Administration — agent use cases"
      valueLabel="Use case"
      usedByHeader="Used by agents"
    />
  );
}
